import React, { useState, useEffect, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import ElementsDataService from '../../api/entitiesDataServices/ElementsDataService.js'
import * as XLSX from 'xlsx'

const T = {
    bg:'#f1f5f9', card:'#ffffff', border:'#e8edf5',
    blue:'#3b82f6', blueD:'#2563eb', navy:'#0f172a',
    txt:'#1e293b', txt2:'#64748b', muted:'#94a3b8',
    green:'#16a34a', greenBg:'#f0fdf4', greenBdr:'#bbf7d0',
    red:'#dc2626', redBg:'#fef2f2', redBdr:'#fecaca',
    warn:'#f59e0b', warnBg:'#fffbeb', warnBdr:'#fde68a',
    r:16, rsm:10,
    shadow:'0 1px 3px rgba(15,23,42,.06)',
    font:"'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
}

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

/* ─── SingleSelect con búsqueda ─── */
function SociedadSelect({ options, value, onChange, loading }) {
    const [open,  setOpen]  = useState(false)
    const [query, setQuery] = useState('')
    const inputRef     = useRef(null)
    const containerRef = useRef(null)

    useEffect(() => {
        const h = e => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false) }
        document.addEventListener('mousedown', h)
        return () => document.removeEventListener('mousedown', h)
    }, [])

    const filtered = options.filter(o =>
        (`${o.razonSocial} ${o.rfc}`).toLowerCase().includes(query.toLowerCase())
    )

    const handleSelect = o => { onChange(o); setOpen(false); setQuery('') }

    return (
        <div ref={containerRef} style={{ position:'relative', flex:1 }}>
            <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.txt2,
                marginBottom:4, textTransform:'uppercase', letterSpacing:'.05em' }}>Sociedad / RFC</label>
            <div onClick={() => { if (!loading) { setOpen(o => !o); setTimeout(() => inputRef.current?.focus(), 50) } }}
                style={{ minHeight:40, padding:'6px 12px', borderRadius:T.rsm,
                    border:`1px solid ${open ? T.blue : T.border}`, background: loading ? T.bg : T.card,
                    cursor: loading ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center',
                    justifyContent:'space-between', gap:8,
                    boxShadow: open ? `0 0 0 3px ${T.blue}20` : T.shadow, transition:'all .15s' }}>
                {loading
                    ? <span style={{ fontSize:13, color:T.muted }}>Cargando sociedades...</span>
                    : value
                        ? <span style={{ fontSize:13, color:T.txt, fontWeight:500 }}>
                            {value.razonSocial}
                            <span style={{ color:T.muted, fontFamily:'monospace', fontSize:12, marginLeft:8 }}>{value.rfc}</span>
                          </span>
                        : <span style={{ fontSize:13, color:T.muted }}>Selecciona una sociedad...</span>
                }
                <span style={{ color:T.muted, fontSize:12 }}>{open ? '▲' : '▼'}</span>
            </div>
            {open && (
                <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:200, marginTop:4,
                    background:T.card, border:`1px solid ${T.border}`, borderRadius:T.rsm,
                    boxShadow:'0 8px 24px rgba(15,23,42,.12)', overflow:'hidden' }}>
                    <div style={{ padding:'8px 12px', borderBottom:`1px solid ${T.border}` }}>
                        <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
                            placeholder="Buscar por razón social o RFC..."
                            onMouseDown={e => e.stopPropagation()}
                            style={{ width:'100%', border:'none', outline:'none', fontSize:13,
                                color:T.txt, fontFamily:T.font, background:'transparent' }} />
                    </div>
                    <div style={{ maxHeight:260, overflowY:'auto' }}>
                        {filtered.length === 0
                            ? <div style={{ padding:'12px 14px', fontSize:13, color:T.muted }}>Sin resultados</div>
                            : filtered.map(o => (
                                <div key={o.rfc} onMouseDown={e => { e.preventDefault(); handleSelect(o) }}
                                    style={{ padding:'10px 14px', fontSize:13, cursor:'pointer',
                                        background: value?.rfc === o.rfc ? `${T.blue}08` : 'transparent',
                                        color: value?.rfc === o.rfc ? T.blue : T.txt,
                                        fontWeight: value?.rfc === o.rfc ? 600 : 400,
                                        borderBottom:`1px solid ${T.border}` }}>
                                    <span style={{ fontWeight:500 }}>{o.razonSocial}</span>
                                    <span style={{ color:T.muted, fontFamily:'monospace', fontSize:11, marginLeft:8 }}>{o.rfc}</span>
                                </div>
                            ))
                        }
                    </div>
                </div>
            )}
        </div>
    )
}

export default function GetCFDIs() {
    const history  = useHistory()
    const { id }   = useParams()

    const [allSociedades, setAllSociedades] = useState([])
    const [loadingCat,    setLoadingCat]    = useState(true)
    const [sociedad,      setSociedad]      = useState(null)
    const [ejercicio,     setEjercicio]     = useState(new Date().getFullYear())
    const [periodo,       setPeriodo]       = useState('all')
    const [loading,       setLoading]       = useState(false)
    const [cfdis,         setCfdis]         = useState([])
    const [error,         setError]         = useState('')
    const [hasFetched,    setHasFetched]    = useState(false)

    useEffect(() => {
        ElementsDataService.retrieveAllElements('sociedades')
            .then(r => setAllSociedades(
                (r.data || []).map(s => ({ id:s.id, razonSocial:s.razonSocial, rfc:s.rfc }))
            ))
            .finally(() => setLoadingCat(false))
    }, [])

    const handleDescargar = () => {
        if (!sociedad) { setError('Selecciona una sociedad primero'); return }
        setError('')
        setLoading(true)
        setCfdis([])
        setHasFetched(false)

        ElementsDataService.retrieveAllElements(`cfdis/getcfdis/${sociedad.rfc}/${periodo}/${ejercicio}`)
            .then(r => {
                const data = r.data || []
                setCfdis(data)
                setHasFetched(true)
                if (data.length === 0) setError(`Sin CFDIs para ${sociedad.rfc} · ${periodo === 'all' ? 'todos los meses' : periodo} ${ejercicio}`)
            })
            .catch(() => setError('Error al obtener los CFDIs'))
            .finally(() => setLoading(false))
    }

    /* ── Exportar CSV con separador ~ (formato SAT) ── */
    const downloadCSV = () => {
        const header = 'Uuid~RfcEmisor~NombreEmisor~RfcReceptor~NombreReceptor~RfcPac~FechaEmision~FechaCertificacionSat~Monto~EfectoComprobante~Estatus~FechaCancelacion\n'
        const rows   = cfdis.map(c =>
            [c.uuid, c.rfcEmisor, c.nombreEmisor, c.rfcReceptor, c.nombreReceptor,
             c.rfcPac, c.fechaEmision, c.fechaCertificacionSat, c.monto,
             c.efectoComprobante, c.estatus, c.fechaCancelacion].join('~')
        ).join('\n')
        const blob = new Blob(['\uFEFF' + header + rows], { type:'text/plain;charset=utf-8;' })
        const url  = URL.createObjectURL(blob)
        const a    = document.createElement('a')
        a.href     = url
        a.download = `CFDIs_${sociedad.rfc}_${periodo}_${ejercicio}.csv`
        document.body.appendChild(a); a.click()
        document.body.removeChild(a); URL.revokeObjectURL(url)
    }

    /* ── Exportar Excel ── */
    const downloadExcel = () => {
        const data = cfdis.map(c => ({
            'UUID':                   c.uuid,
            'RFC Emisor':             c.rfcEmisor,
            'Nombre Emisor':          c.nombreEmisor,
            'RFC Receptor':           c.rfcReceptor,
            'Nombre Receptor':        c.nombreReceptor,
            'RFC PAC':                c.rfcPac,
            'Fecha Emisión':          c.fechaEmision,
            'Fecha Certificación SAT':c.fechaCertificacionSat,
            'Monto':                  c.monto,
            'Efecto Comprobante':     c.efectoComprobante,
            'Estatus':                c.estatus,
            'Fecha Cancelación':      c.fechaCancelacion,
        }))
        const ws = XLSX.utils.json_to_sheet(data)
        ws['!cols'] = [
            {wch:38},{wch:16},{wch:36},{wch:16},{wch:36},
            {wch:16},{wch:22},{wch:22},{wch:14},{wch:18},{wch:12},{wch:22},
        ]
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, sociedad?.rfc || 'CFDIs')
        XLSX.writeFile(wb, `CFDIs_${sociedad?.rfc}_${periodo}_${ejercicio}.xlsx`)
    }

    /* ── estilos tabla ── */
    const thS = { padding:'9px 12px', fontSize:11, fontWeight:700, color:T.txt2, textTransform:'uppercase',
        letterSpacing:'.04em', whiteSpace:'nowrap', borderBottom:`2px solid ${T.border}`,
        background:'#f8fafc', borderRight:`1px solid ${T.border}`, textAlign:'left' }
    const tdS = { padding:'9px 12px', fontSize:12, color:T.txt, borderBottom:`1px solid ${T.border}`,
        borderRight:`1px solid ${T.border}`, whiteSpace:'nowrap', verticalAlign:'middle' }

    return (
        <div style={{ minHeight:'100vh', background:T.bg, fontFamily:T.font, padding:'24px 28px 64px' }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                @keyframes spin { to { transform:rotate(360deg) } }
                tr:hover td { background: #eff6ff !important; }
            `}</style>

            <div style={{ maxWidth:1200, margin:'0 auto' }}>

                {/* ── Header ── */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <button onClick={() => history.goBack()}
                            style={{ width:34, height:34, borderRadius:9, border:`1px solid ${T.border}`,
                                background:T.card, cursor:'pointer', fontSize:16, color:T.txt2,
                                display:'flex', alignItems:'center', justifyContent:'center',
                                boxShadow:T.shadow, flexShrink:0, fontFamily:T.font, transition:'all .15s' }}
                            onMouseEnter={e => { e.currentTarget.style.background=T.navy; e.currentTarget.style.color='white' }}
                            onMouseLeave={e => { e.currentTarget.style.background=T.card; e.currentTarget.style.color=T.txt2 }}>
                            ‹
                        </button>
                        <div>
                            <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:T.navy, letterSpacing:'-0.025em' }}>
                                Obtener CFDIs
                            </h1>
                            <p style={{ margin:'3px 0 0', fontSize:13, color:T.txt2 }}>
                                {sociedad ? `${sociedad.razonSocial} · ${sociedad.rfc}` : 'Selecciona sociedad, ejercicio y periodo'}
                            </p>
                        </div>
                    </div>
                    {/* Botones de descarga — solo cuando hay resultados */}
                    {cfdis.length > 0 && (
                        <div style={{ display:'flex', gap:8 }}>
                            <button onClick={downloadCSV}
                                style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px',
                                    borderRadius:T.rsm, border:`1px solid ${T.border}`, background:T.card,
                                    color:T.txt, fontSize:13, fontWeight:600, cursor:'pointer',
                                    fontFamily:T.font, boxShadow:T.shadow }}>
                                📄 CSV (formato SAT)
                            </button>
                            <button onClick={downloadExcel}
                                style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px',
                                    borderRadius:T.rsm, border:'none', background:T.blue, color:'white',
                                    fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:T.font,
                                    boxShadow:`0 4px 12px ${T.blue}40` }}>
                                📥 Excel
                            </button>
                        </div>
                    )}
                </div>

                {/* ── Panel de filtros ── */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r,
                    boxShadow:T.shadow, padding:'16px 20px', marginBottom:16 }}>
                    <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
                        {/* Selector sociedad */}
                        <div style={{ flex:3, minWidth:280 }}>
                            <SociedadSelect options={allSociedades} value={sociedad}
                                onChange={s => { setSociedad(s); setCfdis([]); setHasFetched(false); setError('') }}
                                loading={loadingCat} />
                        </div>

                        {/* Ejercicio */}
                        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            <label style={{ fontSize:11, fontWeight:600, color:T.txt2, textTransform:'uppercase', letterSpacing:'.05em' }}>Ejercicio</label>
                            <input type="number" min="1900" max="2099" step="1" value={ejercicio}
                                onChange={e => setEjercicio(Number(e.target.value))}
                                style={{ width:100, padding:'8px 10px', borderRadius:T.rsm, border:`1px solid ${T.border}`,
                                    fontSize:13, color:T.txt, fontFamily:T.font, outline:'none', background:T.card }} />
                        </div>

                        {/* Periodo */}
                        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            <label style={{ fontSize:11, fontWeight:600, color:T.txt2, textTransform:'uppercase', letterSpacing:'.05em' }}>Periodo</label>
                            <select value={periodo} onChange={e => setPeriodo(e.target.value)}
                                style={{ padding:'8px 12px', borderRadius:T.rsm, border:`1px solid ${T.border}`,
                                    fontSize:13, color:T.txt, fontFamily:T.font, background:T.card,
                                    cursor:'pointer', outline:'none', minWidth:130 }}>
                                <option value="all">Todos los meses</option>
                                {MESES.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>

                        {/* Botón */}
                        <button onClick={handleDescargar} disabled={loading || loadingCat}
                            style={{ padding:'9px 20px', borderRadius:T.rsm, border:'none',
                                background: loading || loadingCat ? T.muted : T.blue,
                                color:'white', fontSize:13, fontWeight:700,
                                cursor: loading || loadingCat ? 'not-allowed' : 'pointer',
                                fontFamily:T.font, display:'inline-flex', alignItems:'center', gap:8,
                                boxShadow: !loading && !loadingCat ? `0 4px 12px ${T.blue}40` : 'none',
                                alignSelf:'flex-end' }}>
                            {loading ? (
                                <>
                                    <div style={{ width:14, height:14, border:'2px solid #ffffff40',
                                        borderTopColor:'white', borderRadius:'50%',
                                        animation:'spin .7s linear infinite' }} />
                                    Obteniendo...
                                </>
                            ) : '🔍 Obtener CFDIs'}
                        </button>
                    </div>

                    {/* Error */}
                    {error && (
                        <div style={{ marginTop:12, padding:'10px 14px', borderRadius:T.rsm,
                            background:T.warnBg, border:`1px solid ${T.warnBdr}`,
                            fontSize:13, color:T.warn, fontWeight:600, display:'flex', alignItems:'center', gap:8 }}>
                            ⚠️ {error}
                        </div>
                    )}
                </div>

                {/* ── Tabla de resultados ── */}
                {hasFetched && cfdis.length > 0 && (
                    <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, boxShadow:T.shadow, overflow:'hidden' }}>
                        <div style={{ padding:'12px 20px', borderBottom:`1px solid ${T.border}`,
                            display:'flex', alignItems:'center', gap:8 }}>
                            <span>🧾</span>
                            <h2 style={{ margin:0, fontSize:14, fontWeight:700, color:T.txt }}>CFDIs</h2>
                            <span style={{ fontSize:12, fontWeight:700, color:T.blue, background:`${T.blue}12`,
                                border:`1px solid ${T.blue}20`, borderRadius:10, padding:'1px 8px' }}>
                                {cfdis.length}
                            </span>
                            <span style={{ fontSize:12, color:T.muted, marginLeft:4 }}>
                                {sociedad?.rfc} · {periodo === 'all' ? 'todos los meses' : periodo} {ejercicio}
                            </span>
                        </div>
                        <div style={{ overflowX:'auto' }}>
                            <table style={{ borderCollapse:'collapse', width:'100%', minWidth:1100 }}>
                                <thead>
                                    <tr>
                                        {['UUID','RFC Emisor','Nombre Emisor','RFC Receptor','Nombre Receptor',
                                          'RFC PAC','Fecha Emisión','Fecha Cert. SAT','Monto','Efecto','Estatus','F. Cancelación'
                                        ].map(h => <th key={h} style={thS}>{h}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {cfdis.map((c, i) => {
                                        const cancelado = c.estatus === 'Cancelado' || c.estatus === '0'
                                        return (
                                            <tr key={c.uuid ?? i}>
                                                <td style={{ ...tdS, background:i%2===0?T.card:T.bg, fontFamily:'monospace', fontSize:11, color:T.txt2 }}>{c.uuid}</td>
                                                <td style={{ ...tdS, background:i%2===0?T.card:T.bg, fontFamily:'monospace', fontSize:11 }}>{c.rfcEmisor}</td>
                                                <td style={{ ...tdS, background:i%2===0?T.card:T.bg, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis' }}>{c.nombreEmisor}</td>
                                                <td style={{ ...tdS, background:i%2===0?T.card:T.bg, fontFamily:'monospace', fontSize:11 }}>{c.rfcReceptor}</td>
                                                <td style={{ ...tdS, background:i%2===0?T.card:T.bg, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis' }}>{c.nombreReceptor}</td>
                                                <td style={{ ...tdS, background:i%2===0?T.card:T.bg, fontFamily:'monospace', fontSize:11 }}>{c.rfcPac}</td>
                                                <td style={{ ...tdS, background:i%2===0?T.card:T.bg, fontSize:11 }}>{c.fechaEmision}</td>
                                                <td style={{ ...tdS, background:i%2===0?T.card:T.bg, fontSize:11 }}>{c.fechaCertificacionSat}</td>
                                                <td style={{ ...tdS, background:i%2===0?T.card:T.bg, textAlign:'right', fontVariantNumeric:'tabular-nums', fontFamily:'monospace' }}>
                                                    {Number(c.monto).toLocaleString('es-MX', { minimumFractionDigits:2 })}
                                                </td>
                                                <td style={{ ...tdS, background:i%2===0?T.card:T.bg }}>
                                                    <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600,
                                                        background: c.efectoComprobante === 'I' ? T.greenBg : '#f0f4ff',
                                                        color: c.efectoComprobante === 'I' ? T.green : T.blueD,
                                                        border: `1px solid ${c.efectoComprobante === 'I' ? T.greenBdr : '#c7d7fd'}` }}>
                                                        {c.efectoComprobante === 'I' ? 'Ingreso' : c.efectoComprobante === 'E' ? 'Egreso' : c.efectoComprobante === 'N' ? 'Nómina' : c.efectoComprobante}
                                                    </span>
                                                </td>
                                                <td style={{ ...tdS, background:i%2===0?T.card:T.bg }}>
                                                    <span style={{ display:'inline-block', padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600,
                                                        background: cancelado ? T.redBg : T.greenBg,
                                                        color: cancelado ? T.red : T.green,
                                                        border: `1px solid ${cancelado ? T.redBdr : T.greenBdr}` }}>
                                                        {cancelado ? 'Cancelado' : 'Vigente'}
                                                    </span>
                                                </td>
                                                <td style={{ ...tdS, background:i%2===0?T.card:T.bg, fontSize:11, color:T.txt2 }}>{c.fechaCancelacion || '—'}</td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

            </div>
        </div>
    )
}
